import { getStateStore } from '../../stores';
import config from '../../../config.json';
import { kickPlayer } from '../peers/host';

type InputType = 'keyboard' | 'mouse' | 'gamepad' | 'touch';

const store = getStateStore('ipc');

const RECENT_INPUT_TIMEOUT = 700; // 0.7 seconds
const AFK_TIMEOUT = 1000 * 10; // 10 seconds
const INACTIVITY_TIMEOUT = config.PLAYER_INACTIVITY_TIMEOUT;

const activityListeners: {
  [playerId: string]: {
    afk: NodeJS.Timeout | null;
    afkKick: NodeJS.Timeout | null;
    mouse: NodeJS.Timeout | null;
    keyboard: NodeJS.Timeout | null;
    touch: NodeJS.Timeout | null;
    gamepad: {
      0?: NodeJS.Timeout | null;
      1?: NodeJS.Timeout | null;
      2?: NodeJS.Timeout | null;
      3?: NodeJS.Timeout | null;
    };
  };
} = {};

export const startAFKTimeout = (playerId: string) => {
  if (!activityListeners[playerId]) {
    activityListeners[playerId] = {
      afk: null,
      afkKick: null,
      mouse: null,
      keyboard: null,
      touch: null,
      gamepad: {},
    };
  }

  if (activityListeners[playerId]?.afk) {
    clearTimeout(activityListeners[playerId].afk);
  }
  activityListeners[playerId].afk = setTimeout(() => {
    const newActivityState = structuredClone(store.playerActivityState.currentState.players[playerId]);
    newActivityState.afk = true;

    store.playerActivityState.updateState({
      ...store.playerActivityState.currentState,
      players: {
        ...store.playerActivityState.currentState.players,
        [playerId]: newActivityState,
      },
    });
    console.log(`[PlayerActivity] Player ${playerId} is AFK`);

    // Start per-player AFK kick timer
    if (activityListeners[playerId]?.afkKick) {
      clearTimeout(activityListeners[playerId].afkKick);
    }
    activityListeners[playerId].afkKick = setTimeout(async () => {
      const stillAfk = store.playerActivityState.currentState.players[playerId]?.afk;
      if (stillAfk) {
        console.log(`[PlayerActivity] Kicking player ${playerId} due to AFK timeout`);
        try {
          kickPlayer(playerId, 'kicked:afk');
        } catch (err) {
          console.error('[PlayerActivity] Failed to kick AFK player', playerId, err);
        }
        cleanupActivityListeners(playerId);
      }
    }, INACTIVITY_TIMEOUT);
  }, AFK_TIMEOUT);
};

export const cleanupActivityListeners = (playerId: string) => {
  if (!activityListeners[playerId]) {
    console.error(`[PlayerActivity] Player ${playerId} not found`);
    return;
  }

  if (activityListeners[playerId]?.afk) {
    clearTimeout(activityListeners[playerId].afk);
    activityListeners[playerId].afk = null;
  }
  if (activityListeners[playerId]?.afkKick) {
    clearTimeout(activityListeners[playerId].afkKick);
    activityListeners[playerId].afkKick = null;
  }

  if (activityListeners[playerId]?.mouse) {
    clearTimeout(activityListeners[playerId].mouse);
    activityListeners[playerId].mouse = null;
  }

  if (activityListeners[playerId]?.keyboard) {
    clearTimeout(activityListeners[playerId].keyboard);
    activityListeners[playerId].keyboard = null;
  }

  if (activityListeners[playerId]?.touch) {
    clearTimeout(activityListeners[playerId].touch);
    activityListeners[playerId].touch = null;
  }

  for (const index in activityListeners[playerId].gamepad) {
    if (activityListeners[playerId].gamepad[index]) {
      clearTimeout(activityListeners[playerId].gamepad[index]);
      activityListeners[playerId].gamepad[index] = null;
    }
  }
};

export const notifyInput = (playerId: string, inputType: InputType, gamepadIndex?: number) => {
  const playerActivity = store.playerActivityState?.currentState.players[playerId];
  if (!playerActivity) {
    console.error(`[PlayerActivity] Player ${playerId} not found`);
    return;
  }

  if (!activityListeners[playerId]) {
    activityListeners[playerId] = {
      afk: null,
      afkKick: null,
      mouse: null,
      keyboard: null,
      touch: null,
      gamepad: {},
    };
  }
  if (activityListeners[playerId]?.afk) {
    clearTimeout(activityListeners[playerId].afk);
  }
  if (activityListeners[playerId]?.afkKick) {
    clearTimeout(activityListeners[playerId].afkKick);
    activityListeners[playerId].afkKick = null;
  }
  activityListeners[playerId].afk = setTimeout(() => {
    const newActivityState = structuredClone(store.playerActivityState.currentState.players[playerId]);
    newActivityState.afk = true;

    store.playerActivityState.updateState({
      ...store.playerActivityState.currentState,
      players: {
        ...store.playerActivityState.currentState.players,
        [playerId]: newActivityState,
      },
    });
    console.log(`[PlayerActivity] Player ${playerId} is AFK`);
  }, AFK_TIMEOUT);

  switch (inputType) {
    case 'keyboard': {
      playerActivity.keyboard.hasSentInput = true;
      playerActivity.keyboard.hasRecentInput = true;

      if (activityListeners[playerId]?.keyboard) {
        clearTimeout(activityListeners[playerId].keyboard);
      }
      activityListeners[playerId].keyboard = setTimeout(() => {
        const newKeyboardActivityState = structuredClone(store.playerActivityState.currentState.players[playerId]);
        newKeyboardActivityState.keyboard.hasRecentInput = false;
        store.playerActivityState.updateState({
          ...store.playerActivityState.currentState,
          players: {
            ...store.playerActivityState.currentState.players,
            [playerId]: newKeyboardActivityState,
          },
        });
      }, RECENT_INPUT_TIMEOUT);
      break;
    }
    case 'mouse': {
      playerActivity.mouse.hasSentInput = true;
      playerActivity.mouse.hasRecentInput = true;

      if (activityListeners[playerId]?.mouse) {
        clearTimeout(activityListeners[playerId].mouse);
      }
      activityListeners[playerId].mouse = setTimeout(() => {
        const newMouseActivityState = structuredClone(store.playerActivityState.currentState.players[playerId]);
        newMouseActivityState.mouse.hasRecentInput = false;
        store.playerActivityState.updateState({
          ...store.playerActivityState.currentState,
          players: {
            ...store.playerActivityState.currentState.players,
            [playerId]: newMouseActivityState,
          },
        });
      }, RECENT_INPUT_TIMEOUT);
      break;
    }
    case 'touch': {
      // Piggyback on mouse activity tracking (same behavior)
      playerActivity.mouse.hasSentInput = true;
      playerActivity.mouse.hasRecentInput = true;

      if (activityListeners[playerId]?.touch) {
        clearTimeout(activityListeners[playerId].touch);
      }
      activityListeners[playerId].touch = setTimeout(() => {
        const newTouchActivityState = structuredClone(store.playerActivityState.currentState.players[playerId]);
        newTouchActivityState.mouse.hasRecentInput = false;
        store.playerActivityState.updateState({
          ...store.playerActivityState.currentState,
          players: {
            ...store.playerActivityState.currentState.players,
            [playerId]: newTouchActivityState,
          },
        });
      }, RECENT_INPUT_TIMEOUT);
      break;
    }
    case 'gamepad': {
      if (gamepadIndex !== undefined) {
        if (!playerActivity.gamepad[gamepadIndex]) {
          playerActivity.gamepad[gamepadIndex] = { hasSentInput: true, hasRecentInput: true };
        } else {
          playerActivity.gamepad[gamepadIndex].hasSentInput = true;
          playerActivity.gamepad[gamepadIndex].hasRecentInput = true;
        }

        if (activityListeners[playerId]?.gamepad?.[gamepadIndex]) {
          clearTimeout(activityListeners[playerId].gamepad[gamepadIndex]);
        }
        activityListeners[playerId].gamepad[gamepadIndex] = setTimeout(() => {
          const newGamepadActivityState = structuredClone(store.playerActivityState.currentState.players[playerId]);
          newGamepadActivityState.gamepad[gamepadIndex].hasRecentInput = false;
          store.playerActivityState.updateState({
            ...store.playerActivityState.currentState,
            players: {
              ...store.playerActivityState.currentState.players,
              [playerId]: newGamepadActivityState,
            },
          });
        }, RECENT_INPUT_TIMEOUT);
      } else {
        console.error(`[PlayerActivity] Gamepad index is required for gamepad input`);
      }
      break;
    }
    default:
      console.error(`[PlayerActivity] Unknown input type: ${inputType}`);
  }

  playerActivity.afk = false;
  store.playerActivityState.updateState({
    ...store.playerActivityState.currentState,
    inactive: false,
    players: {
      ...store.playerActivityState.currentState.players,
      [playerId]: playerActivity,
    },
  });
};

export const clearActivityListener = (playerId: string, inputType: InputType, gamepadIndex?: number) => {
  const playerAssignments = structuredClone(store.playerState.currentState.players[playerId]);
  if (!playerAssignments) {
    console.error(`[PlayerActivity] Player ${playerId} not found`);
    return;
  }
  const newActivityState = structuredClone(store.playerActivityState.currentState.players[playerId]);

  let shouldClearAfk = true;
  if (playerAssignments.access.keyboard) shouldClearAfk = false;
  if (playerAssignments.access.mouse) shouldClearAfk = false;
  if (playerAssignments.access.touch) shouldClearAfk = false;
  if (playerAssignments.access.gamepad) {
    const gamepadAssignments = store.gamepadState.currentState.playerMappings[playerId];
    if (!gamepadAssignments) {
      return;
    }
    shouldClearAfk = gamepadAssignments.gamepadIndexes.length !== 0;
  }
  if (shouldClearAfk) {
    if (activityListeners[playerId]?.afk) {
      clearTimeout(activityListeners[playerId].afk);
      activityListeners[playerId].afk = null;
      newActivityState.afk = false;
    }
    if (activityListeners[playerId]?.afkKick) {
      clearTimeout(activityListeners[playerId].afkKick);
      activityListeners[playerId].afkKick = null;
    }
  }

  switch (inputType) {
    case 'keyboard': {
      if (activityListeners[playerId]?.keyboard) {
        clearTimeout(activityListeners[playerId].keyboard);
        activityListeners[playerId].keyboard = null;
        newActivityState.keyboard.hasRecentInput = false;
        newActivityState.keyboard.hasSentInput = false;
      }
      break;
    }
    case 'mouse': {
      if (activityListeners[playerId]?.mouse) {
        clearTimeout(activityListeners[playerId].mouse);
        activityListeners[playerId].mouse = null;
        newActivityState.mouse.hasRecentInput = false;
        newActivityState.mouse.hasSentInput = false;
      }
      break;
    }
    case 'touch': {
      // Piggyback on mouse activity tracking (same behavior)
      if (activityListeners[playerId]?.touch) {
        clearTimeout(activityListeners[playerId].touch);
        activityListeners[playerId].touch = null;
        newActivityState.mouse.hasRecentInput = false;
        newActivityState.mouse.hasSentInput = false;
      }
      break;
    }
    case 'gamepad': {
      if (gamepadIndex !== undefined && activityListeners[playerId]?.gamepad?.[gamepadIndex]) {
        clearTimeout(activityListeners[playerId].gamepad[gamepadIndex]);
        activityListeners[playerId].gamepad[gamepadIndex] = null;
        newActivityState.gamepad[gamepadIndex].hasRecentInput = false;
        newActivityState.gamepad[gamepadIndex].hasSentInput = false;
      } else if (gamepadIndex === undefined) {
        // Clear all gamepad activity listeners
        for (const index in activityListeners[playerId]?.gamepad) {
          if (activityListeners[playerId]?.gamepad?.[index]) {
            clearTimeout(activityListeners[playerId].gamepad[index]);
            activityListeners[playerId].gamepad[index] = null;
            newActivityState.gamepad[index].hasRecentInput = false;
            newActivityState.gamepad[index].hasSentInput = false;
          }
        }
      }
      break;
    }
    default:
      console.error(`[PlayerActivity] Unknown input type: ${inputType}`);
  }

  store.playerActivityState.updateState({
    ...store.playerActivityState.currentState,
    players: {
      ...store.playerActivityState.currentState.players,
      [playerId]: newActivityState,
    },
  });
};

export const checkPlayerActivity = () => {
  // Global inactivity tracking removed; per-player AFK is handled elsewhere
  return;
};

export const startInactivityTimeout = () => {
  // No-op (deprecated)
  return;
};

export const clearInactivityTimeout = () => {
  // No-op (deprecated)
  return;
};
